/* Клиент API этапа 2 «Контакты»: контакты, этапы жизненного цикла, теги, поля карточки, импорт CSV и экспорт.
   Права проверяет сервер: агент видит только свои контакты; этапы и поля меняют владелец/администратор,
   теги, импорт, удаление и массовые действия — менеджер и выше; экспорт — администратор или доп. право «Экспорт». */
import { api } from "@/lib/api";
import type { Ads } from "./ads";
import type { Conversation, Message } from "./inbox-api";
import { t } from "@/lib/i18n";

export type ContactTag = { id: string; name: string; emoji: string; color: string; groupId: string | null; group: string | null };
/** группа тегов — измерение со списком значений, как Reporting Tag в Zoho Books; у контакта по группе одно значение */
export type TagGroup = { id: string; name: string; emoji: string; color: string; description: string; position: number; options: number; count: number; createdAt: string };

export type Contact = {
  id: string;
  firstName: string;
  lastName: string;
  /** имя для показа: «Имя Фамилия», иначе телефон или почта */
  name: string;
  phone: string | null;
  email: string | null;
  company: string;
  /** код страны: AE, RU… */
  country: string;
  language: string;
  stageId: string | null;
  ownerId: string | null;
  teamId: string | null;
  source: string;
  blocked: boolean;
  externalId: string | null;
  /** WhatsApp, Telegram и доп. номера — приходят только в карточке (GET /contacts/{id}) */
  ads?: Ads | null;
  sourceFirst?: { channel?: string; channelId?: string; at?: string } | null;
  identities?: { kind: "whatsapp" | "telegram" | "phone" | string; value: string }[];
  /** значения своих полей по ключу поля */
  fields: Record<string, unknown>;
  tags: ContactTag[];
  lastActivityAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContactInput = Partial<{
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  company: string;
  country: string;
  language: string;
  stageId: string | null;
  ownerId: string | null;
  teamId: string | null;
  blocked: boolean;
  fields: Record<string, unknown>;
  tagIds: string[];
}>;

export type ContactQuery = Partial<{
  q: string;
  /** id этапа или "none" */
  stage: string;
  segment: Segment;
  tag: string;
  /** id пользователя, "me" или "none" */
  owner: string;
  sort: "created" | "name" | "activity";
  /** только контакты, созданные или обновлённые этим импортом */
  import_id: number;
  limit: number;
  offset: number;
}>;

export type Segment = "fresh" | "sleeping" | "tagged" | "country" | "lang" | "blocked";

export type Folders = { all: number; mine: number; free: number; stages: Record<string, number>; segments: Record<Segment, number> };

export type Stage = {
  id: string;
  name: string;
  description: string;
  icon: string;
  /** группа «потерянные» */
  lost: boolean;
  isDefault: boolean;
  isWon: boolean;
  position: number;
  count: number;
};

export type Tag = ContactTag & { description: string; count: number; createdAt: string };

export type FieldType = "text" | "number" | "email" | "phone" | "telegram" | "date" | "multi" | "image" | "bool";

export type ContactFieldDef = {
  id: string;
  key: string;
  label: string;
  description: string;
  type: FieldType;
  visibility: "always" | "hidden" | "admin";
  options: string[];
  /** встроенное поле: можно переименовать и скрыть, нельзя удалить и сменить тип */
  system: boolean;
  position: number;
  createdAt: string;
};

export type BulkAction = "stage" | "owner" | "team" | "tags_add" | "tags_remove" | "block" | "unblock" | "delete";

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const contactsApi = {
  list: (q: ContactQuery = {}) => api<{ items: Contact[]; total: number }>(`/contacts${qs(q)}`),
  folders: () => api<Folders>("/contacts/folders"),
  get: (id: string) => api<Contact>(`/contacts/${id}`),
  conversations: (id: string) => api<{ items: Conversation[]; total: number }>(`/contacts/${id}/conversations`),
  searchMessages: (id: string, q: string) => api<{ items: (Message & { channel: string; conversationName: string })[]; total: number }>(`/contacts/${id}/messages?q=${encodeURIComponent(q)}`),
  create: (body: ContactInput) => api<Contact>("/contacts", { method: "POST", json: body }),
  update: (id: string, body: ContactInput) => api<Contact>(`/contacts/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/contacts/${id}`, { method: "DELETE" }),
  bulk: (ids: string[], action: BulkAction, value?: string | null) => api<{ affected: number }>("/contacts/bulk", { method: "POST", json: { ids, action, value } }),
};

export const stagesApi = {
  list: () => api<Stage[]>("/lifecycle"),
  /** все изменения этапов возвращают обновлённый список */
  create: (body: Partial<Pick<Stage, "name" | "description" | "icon" | "lost" | "isDefault" | "isWon">>) => api<Stage[]>("/lifecycle", { method: "POST", json: body }),
  update: (id: string, body: Partial<Pick<Stage, "name" | "description" | "icon" | "lost" | "isDefault" | "isWon">>) =>
    api<Stage[]>(`/lifecycle/${id}`, { method: "PATCH", json: body }),
  reorder: (ids: string[]) => api<Stage[]>("/lifecycle/order", { method: "PUT", json: { ids } }),
  /** контакты удаляемого этапа переходят на moveTo или на этап по умолчанию */
  remove: (id: string, moveTo?: string) => api<Stage[]>(`/lifecycle/${id}${qs({ move_to: moveTo })}`, { method: "DELETE" }),
};

export type TagInput = Partial<Pick<Tag, "name" | "emoji" | "color" | "description">> & { groupId?: string | null };
export type TagGroupInput = Partial<Pick<TagGroup, "name" | "emoji" | "color" | "description">> & { values?: string[] };

export const tagsApi = {
  list: () => api<Tag[]>("/tags"),
  create: (body: TagInput) => api<Tag>("/tags", { method: "POST", json: body }),
  update: (id: string, body: TagInput) => api<Tag>(`/tags/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/tags/${id}`, { method: "DELETE" }),
  groups: () => api<TagGroup[]>("/tags/groups"),
  createGroup: (body: TagGroupInput) => api<TagGroup>("/tags/groups", { method: "POST", json: body }),
  updateGroup: (id: string, body: TagGroupInput) => api<TagGroup>(`/tags/groups/${id}`, { method: "PATCH", json: body }),
  removeGroup: (id: string) => api(`/tags/groups/${id}`, { method: "DELETE" }),
};

export const fieldsApi = {
  list: () => api<ContactFieldDef[]>("/fields"),
  /** key можно не передавать — сервер сделает его из названия латиницей */
  create: (body: Partial<Pick<ContactFieldDef, "key" | "label" | "description" | "type" | "visibility" | "options">>) =>
    api<ContactFieldDef>("/fields", { method: "POST", json: body }),
  update: (id: string, body: Partial<Pick<ContactFieldDef, "label" | "description" | "type" | "visibility" | "options">>) =>
    api<ContactFieldDef>(`/fields/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/fields/${id}`, { method: "DELETE" }),
};

export type ImportTarget = { id: string; label: string };
export type ImportCheck = {
  checked: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { line: number; error: string; value?: string }[];
  /** значения колонки «Ответственный», для которых не нашёлся пользователь Hub */
  unknownOwners?: string[];
  unknownCountries?: string[];
};
export type ImportAnalysis = {
  headers: string[];
  sample: string[][];
  rows: number;
  /** заголовок колонки → цель (id из targets) */
  mapping: Record<string, string>;
  targets: ImportTarget[];
  /** распознанный формат файла: выгрузка Zoho CRM */
  preset?: "zoho_crm" | null;
  /** цели, которые можно выбрать для нескольких колонок */
  multi?: string[];
  check?: ImportCheck;
};
export type ImportRun = {
  id: number;
  fileName: string;
  status: "queued" | "running" | "done" | "failed";
  rows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { line: number; error: string; value?: string }[];
  author?: string | null;
  createdAt: string;
  finishedAt: string | null;
};

const fileForm = (file: File, mapping?: Record<string, string>, dial?: string) => {
  const fd = new FormData();
  fd.append("file", file);
  if (mapping) fd.append("mapping", JSON.stringify(mapping));
  if (dial !== undefined) fd.append("dial", dial);
  return fd;
};

export const importsApi = {
  targets: () => api<ImportTarget[]>("/imports/targets"),
  /** без mapping — заголовки и предложенное сопоставление; с mapping — ещё проверка check (без записи в базу) */
  analyze: (file: File, mapping?: Record<string, string>, dial?: string) =>
    api<ImportAnalysis>("/imports/analyze", { method: "POST", body: fileForm(file, mapping, dial) }),
  /** загрузка идёт в фоне; по окончании — уведомление в колокольчике */
  start: (file: File, mapping: Record<string, string>, dial?: string) => api<ImportRun>("/imports", { method: "POST", body: fileForm(file, mapping, dial) }),
  history: () => api<ImportRun[]>("/imports"),
  get: (id: number) => api<ImportRun>(`/imports/${id}`),
  /** CSV пропущенных строк с причиной — исправить и загрузить снова */
  skippedPath: (id: number) => `/imports/${id}/skipped`,
};

export type ExportRun = {
  id: number;
  dataset: string;
  datasetLabel: string;
  status: "queued" | "running" | "done" | "failed";
  rows: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
  /** ссылка на скачивание, пока файл не истёк (24 часа) */
  url: string | null;
  expired: boolean;
  /** кто запросил выгрузку */
  author?: string | null;
};

export const exportsApi = {
  /** сейчас доступен только dataset "contacts"; диалоги и сообщения — с этапом 3 (сервер ответит dataset_not_ready) */
  start: (dataset: string, filters?: ContactQuery) => api<ExportRun>("/exports", { method: "POST", json: { dataset, filters } }),
  history: () => api<ExportRun[]>("/exports"),
};

/** Ошибки строк импорта человеческим языком */
export const importErrorText: Record<string, string> = {
  empty_row: t("пустая строка: нет имени, телефона и почты"),
  bad_phone: t("телефон в неверном формате"),
  bad_email: t("почта в неверном формате"),
  bad_number: t("в числовом поле не число"),
  duplicate_phone: t("телефон уже есть у другого контакта"),
  duplicate_email: t("почта уже есть у другого контакта"),
};

// «Открыть контакты импорта»: экран импорта оставляет фильтр, экран контактов забирает его при открытии
let pendingImport: { id: number; fileName: string } | null = null;
export const showImportContacts = (run: { id: number; fileName: string }) => {
  pendingImport = { id: run.id, fileName: run.fileName };
};
export const takeImportFilter = () => {
  const v = pendingImport;
  pendingImport = null;
  return v;
};

/** открыть карточку контакта из другого раздела (сделка, компания) */
let pendingContact: string | null = null;
export const showContact = (id: string) => {
  pendingContact = id;
};
export const takeContact = () => {
  const id = pendingContact;
  pendingContact = null;
  return id;
};
